/**
 * Impower document upload + tag PATCH for client_injects (gulp bundle).
 * Mirrors src/lib/impower/document-impower-upload.ts — keep in sync when changing upload flow.
 * Placed under components/ so gulp concat loads this before features/ and utils/.
 *
 * Follow-up refactor: taxAssumptionEditor.js still uses inline upload.
 */

const IMPOWER_DOCUMENT_ENDPOINT = '/services/pmp-document/api/v1/documents'
const IMPOWER_DOCUMENT_TAGS_ENDPOINT = '/services/pmp-public/v2/document-tags'
/** Filed tag applied alongside category tags (Cases, contracts, property uploads). */
const IMPOWER_FILED_DOCUMENT_TAG_NAME = 'abgelegt'
const IMPOWER_FILED_DOCUMENT_TAG_DESCRIPTION = 'Dokument erfolgreich zugeordnet und abgelegt'

const buildImpowerApiUrl = (apiBaseUrl, path) => {
  // `undefined`/`null` → fall back to Lux `baseApiUrl`.
  // Explicit `''` → same-origin relative path (Impower page when inject runs there).
  const base =
    apiBaseUrl === undefined || apiBaseUrl === null
      ? typeof baseApiUrl !== 'undefined'
        ? baseApiUrl
        : ''
      : apiBaseUrl
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  if (base) return `${base}${normalizedPath}`
  return normalizedPath
}

const parseDocumentTagsList = (raw) =>
  Array.isArray(raw) ? raw : (raw && raw.content) || []

const uploadImpowerDocument = async ({ file, token, uploadParams, apiBaseUrl }) => {
  const queryParts = []
  if (uploadParams) {
    for (const [key, value] of Object.entries(uploadParams)) {
      if (value != null) {
        queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      }
    }
  }
  const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : ''
  const formData = new FormData()
  formData.append('file', file, file.name)

  const response = await fetch(
    buildImpowerApiUrl(apiBaseUrl, `${IMPOWER_DOCUMENT_ENDPOINT}/upload${queryString}`),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    }
  )

  if (!response.ok) {
    const errorText = await response.text().catch(() => '')
    return { success: false, error: errorText || `Upload failed: ${response.status}` }
  }

  const data = await response.json()
  return { success: true, data }
}

const resolveDocumentTagIds = async ({
  tagNames,
  token,
  apiBaseUrl,
  createMissingTags = false,
  tagDescriptions = {},
}) => {
  const normalized = [...new Set(tagNames.map((n) => String(n).trim()).filter(Boolean))]
  if (!normalized.length) return { success: true, tagIds: [] }

  const tagsResponse = await fetch(buildImpowerApiUrl(apiBaseUrl, IMPOWER_DOCUMENT_TAGS_ENDPOINT), {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!tagsResponse.ok) {
    const errorText = await tagsResponse.text().catch(() => '')
    return { success: false, error: errorText || 'Dokument-Tags konnten nicht geladen werden.' }
  }

  const raw = await tagsResponse.json()
  const tagsList = parseDocumentTagsList(raw)
  const tagIds = []

  for (const tagName of normalized) {
    let tagId = tagsList.find((tag) => tag.name === tagName)?.id
    if (!tagId && createMissingTags) {
      const createTagResponse = await fetch(buildImpowerApiUrl(apiBaseUrl, IMPOWER_DOCUMENT_TAGS_ENDPOINT), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          name: tagName,
          description: tagDescriptions[tagName] || tagName,
        }),
      })
      if (!createTagResponse.ok) {
        return { success: false, error: `Tag "${tagName}" konnte nicht angelegt werden.` }
      }
      tagId = (await createTagResponse.json())?.id
    }
    if (!tagId) {
      return { success: false, error: `Tag "${tagName}" wurde nicht gefunden.` }
    }
    tagIds.push(tagId)
  }

  return { success: true, tagIds }
}

const patchImpowerDocumentTags = async ({
  documents,
  tagNames,
  token,
  apiBaseUrl,
  createMissingTags = false,
  tagDescriptions = {},
  patchFields = {},
}) => {
  const docs = Array.isArray(documents) ? documents.filter(Boolean) : []
  if (!docs.length) return { success: true, documents: [] }
  if (!tagNames?.length) return { success: true, documents: docs }

  const tagResult = await resolveDocumentTagIds({
    tagNames,
    token,
    apiBaseUrl,
    createMissingTags,
    tagDescriptions,
  })
  if (!tagResult.success) return { success: false, error: tagResult.error }

  const updatePayload = docs.map((document) => ({
    ...document,
    ...patchFields,
    tagIds: tagResult.tagIds,
  }))

  const updateResponse = await fetch(buildImpowerApiUrl(apiBaseUrl, IMPOWER_DOCUMENT_ENDPOINT), {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(updatePayload),
  })

  if (!updateResponse.ok) {
    const errorText = await updateResponse.text().catch(() => '')
    return { success: false, error: errorText || 'Tags konnten nicht gesetzt werden.' }
  }

  const updated = await updateResponse.json()
  return { success: true, documents: Array.isArray(updated) ? updated : docs }
}

const uploadImpowerDocumentWithTags = async ({
  file,
  uploadParams,
  tagNames,
  token,
  apiBaseUrl,
  createMissingTags = false,
  tagDescriptions = {},
  patchFields = {},
}) => {
  const uploadResult = await uploadImpowerDocument({ file, token, uploadParams, apiBaseUrl })
  if (!uploadResult.success || !uploadResult.data) {
    return { success: false, error: uploadResult.error || 'Dokument-Upload fehlgeschlagen.' }
  }

  const patchResult = await patchImpowerDocumentTags({
    documents: [uploadResult.data],
    tagNames,
    token,
    apiBaseUrl,
    createMissingTags,
    tagDescriptions,
    patchFields,
  })

  if (!patchResult.success) {
    return { success: false, error: patchResult.error, data: uploadResult.data }
  }

  const updatedDoc =
    patchResult.documents && patchResult.documents.length > 0
      ? patchResult.documents[0]
      : uploadResult.data

  return { success: true, data: updatedDoc }
}

const uploadImpowerDocumentsWithTag = async ({
  files,
  uploadParamsBuilder,
  tagNames,
  token,
  apiBaseUrl,
  createMissingTags = false,
  tagDescriptions = {},
  patchFieldsBuilder,
}) => {
  const uploaded = []
  const failedFileNames = []
  for (const file of files) {
    const uploadParams = typeof uploadParamsBuilder === 'function' ? uploadParamsBuilder(file) : uploadParamsBuilder
    const result = await uploadImpowerDocument({ file, token, uploadParams, apiBaseUrl })
    if (result.success && result.data) uploaded.push(result.data)
    else failedFileNames.push(file.name)
  }

  if (!uploaded.length) {
    return { success: false, error: 'Dokument-Upload fehlgeschlagen.', documents: [], failedFileNames }
  }

  const patchFields =
    typeof patchFieldsBuilder === 'function' ? patchFieldsBuilder(uploaded[0]) : patchFieldsBuilder || {}

  const patchResult = await patchImpowerDocumentTags({
    documents: uploaded.map((document) => ({
      id: document.id,
      name: document.name,
      ...patchFields,
    })),
    tagNames,
    token,
    apiBaseUrl,
    createMissingTags,
    tagDescriptions,
  })

  if (!patchResult.success) {
    return {
      success: false,
      error: patchResult.error,
      documents: uploaded,
      failedFileNames,
    }
  }

  return {
    success: failedFileNames.length === 0,
    documents: patchResult.documents || uploaded,
    failedFileNames,
  }
}
